import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy, Download, Visibility, VisibilityOff, LightMode, DarkMode, UnfoldMore, UnfoldLess } from '@mui/icons-material';
import { DSLConfig } from '../../types/dynamicRouting';
import ReactJson from 'react18-json-view';
import 'react18-json-view/src/style.css';

interface JsonViewerProps {
  config: DSLConfig;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ config }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [collapsed, setCollapsed] = useState(false); // hides/shows the entire viewer panel
  const [viewerTheme, setViewerTheme] = useState<'github' | 'vscode'>('github');
  const [collapseSetting, setCollapseSetting] = useState<boolean | number>(2); // controls node collapse inside viewer

  const jsonString = JSON.stringify(config, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_routing_config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const toggleTheme = () => {
    setViewerTheme(prev => (prev === 'github' ? 'vscode' : 'github'));
  };

  const handleExpandAll = () => setCollapseSetting(false);
  const handleCollapseAll = () => setCollapseSetting(true);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Compiled JSON Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={collapsed ? "Expand JSON" : "Collapse JSON"}>
            <IconButton onClick={toggleCollapsed}>
              {collapsed ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title={`Switch to ${viewerTheme === 'vscode' ? 'Light' : 'Dark'} Theme`}>
            <IconButton onClick={toggleTheme}>
              {viewerTheme === 'vscode' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Expand All">
            <IconButton onClick={handleExpandAll}>
              <UnfoldMore />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse All">
            <IconButton onClick={handleCollapseAll}>
              <UnfoldLess />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy to Clipboard">
            <IconButton onClick={handleCopy}>
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
            size="small"
          >
            Download
          </Button>
        </Box>
      </Box>

      {copySuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          JSON copied to clipboard!
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This is the JSON representation of your configuration. You can copy or download it to use with the routing engine.
      </Typography>

      {!collapsed && (
        <Paper
          sx={{
            p: 2,
            bgcolor: viewerTheme === 'vscode' ? '#1e1e1e' : '#f5f5f5',
            color: viewerTheme === 'vscode' ? '#e0e0e0' : 'inherit',
            maxHeight: '600px',
            overflow: 'auto',
            fontFamily: 'monospace',
          }}
        >
          <ReactJson
            src={config as unknown as object}
            collapsed={collapseSetting}
            enableClipboard={false}
            theme={viewerTheme}
            style={{ backgroundColor: 'transparent' }}
          />
        </Paper>
      )}

      {collapsed && (
        <Alert severity="info">
          JSON view is collapsed. Click the eye icon to expand.
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Configuration Size: {new Blob([jsonString]).size} bytes
        </Typography>
      </Box>
    </Box>
  );
};

export default JsonViewer;
